import React from 'react';
import Button from '../Button';
import { preloadEnquiryDialog, useEnquiryDialog } from './EnquiryProvider';

/**
 * A redesign Button that opens the enquiry dialog ("Check availability →", "Talk to the
 * team →", "Choose Standard →": an in-page action, so the default arrow is '→'). Uses the
 * page-wide dialog of an EnquiryProvider when there is one, else its own (so a page renders
 * and works in a test without a provider).
 *
 * Props:
 * - `role`: 'advertiser' (default) | 'venue'
 * - `planContext`: optional { tier, interval, venue_count, venue_ids } shown in the dialog
 *   and sent with the enquiry
 * - `sourcePath`: optional page path to record (default: the current path)
 * - every Button prop (`variant`, `size`, `arrow`, `fullWidth`, `className`, `children`,
 *   `onClick` — called first; preventDefault() there stops the dialog opening)
 * Hovering or focusing it starts loading the dialog (its own chunk), so it opens at once.
 */
const EnquiryButton = React.forwardRef(function EnquiryButton(
  { role = 'advertiser', planContext, sourcePath, onClick, onPointerEnter, onFocus, children, ...buttonProps },
  ref,
) {
  const { open, dialog } = useEnquiryDialog();
  const handleClick = (e) => {
    if (onClick) onClick(e);
    if (e && e.defaultPrevented) return;
    open({ role, planContext, sourcePath });
  };
  // Fetch the dialog's chunk as soon as the visitor shows intent, so it opens at once.
  const preload = (handler) => (e) => {
    preloadEnquiryDialog();
    if (handler) handler(e);
  };
  return (
    <>
      <Button
        ref={ref}
        aria-haspopup="dialog"
        {...buttonProps}
        onPointerEnter={preload(onPointerEnter)}
        onFocus={preload(onFocus)}
        onClick={handleClick}
      >
        {children}
      </Button>
      {dialog}
    </>
  );
});

export default EnquiryButton;
